import { findLeaf } from './leaf-dom'
import type { SpeechHighlightTarget } from '../types/block-editor.types'

const BLOCK_HIGHLIGHT = 'speech-block'
const WORD_HIGHLIGHT = 'speech-word'

function clearSpeechHighlight() {
  globalThis.CSS?.highlights?.delete(BLOCK_HIGHLIGHT)
  globalThis.CSS?.highlights?.delete(WORD_HIGHLIGHT)
}

/** Paints the unit and word with CSS custom highlights; the DOM is never touched,
 * so reading, answering and editing keep working underneath. Returns what was
 * painted so the caller can keep it in view. */
export function highlightSpeech(root: HTMLElement | null, target: SpeechHighlightTarget | null): { element: HTMLElement; word: Range | null } | null {
  clearSpeechHighlight()
  if (!root || !target) return null
  const leaf = findLeaf(root, target.blockId)
  const element = leaf?.parent ?? Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]')).find(element => element.dataset.blockId === target.blockId)
  if (!element) return null
  const nodes = leaf ? leaf.nodes.flatMap(node => node instanceof Text ? [node] : spokenTextNodes(node as HTMLElement)) : spokenTextNodes(element)
  const word = target.word ? wordRange(element, nodes, target.word.text, target.word.offset) : null
  if (!globalThis.CSS?.highlights) return { element, word }
  // The unit's spoken text only: controls inside a question stay unpainted.
  const block = nodes.map(node => {
    const range = element.ownerDocument.createRange()
    range.selectNodeContents(node)
    return range
  })
  CSS.highlights.set(BLOCK_HIGHLIGHT, new Highlight(...block))
  if (word) CSS.highlights.set(WORD_HIGHLIGHT, new Highlight(word))
  return { element, word }
}

/** Glosses, form controls, buttons and hidden chrome are not spoken text. */
function spokenTextNodes(element: HTMLElement): Text[] {
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: node => !node.textContent?.trim() || node.parentElement?.closest('rt, input, textarea, select, button, label, [role="button"], [aria-hidden="true"]')
      ? NodeFilter.FILTER_REJECT
      : NodeFilter.FILTER_ACCEPT,
  })
  const nodes: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text)
  return nodes
}

function wordRange(element: HTMLElement, nodes: Text[], word: string, offset: number): Range | null {
  const needle = word.replaceAll(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLocaleLowerCase()
  if (!needle) return null
  const starts: number[] = []
  let text = ''
  for (const node of nodes) {
    starts.push(text.length)
    text += node.data
  }
  const haystack = text.toLocaleLowerCase()
  let best = -1
  for (let index = haystack.indexOf(needle); index !== -1; index = haystack.indexOf(needle, index + 1)) {
    if (best === -1 || Math.abs(index - offset) < Math.abs(best - offset)) best = index
  }
  if (best === -1) return null
  const range = element.ownerDocument.createRange()
  const at = (position: number, edge: 'start' | 'end') => {
    // An end on a node boundary belongs to the earlier node; a start to the later one.
    let index = starts.findLastIndex(start => edge === 'end' ? start < position : start <= position)
    if (index === -1) index = 0
    return { node: nodes[index], offset: position - starts[index] }
  }
  const start = at(best, 'start')
  const end = at(best + needle.length, 'end')
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  return range
}
